import clsx from "clsx";
import { format, parseISO } from "date-fns";
import { Button, ScrollView, SelectBox, TextBox } from "devextreme-react";
import {
  Column,
  DataGrid,
  FilterRow,
  Pager,
  Paging,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import { confirm } from "devextreme/ui/dialog";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../../../utils/media-query";
import type { PendingSlot2 } from "../types";

interface AbsenceConfirmPopup2Props {
  visible: boolean;
  onHiding: () => void;
  filteredSlots: PendingSlot2[];
  isHR: boolean;
  departments: string[];
  selectedDepartment: string;
  setSelectedDepartment: (d: string) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  onConfirm: (id: number) => void;
  onReject: (id: number) => void;
  onConfirmAll: (ids: number[]) => void;
  onRejectAll: (ids: number[]) => void;
}

function formatSlotDate(value: Date | string | undefined): string {
  if (!value) return "";
  try {
    const d = value instanceof Date ? value : parseISO(value);
    return format(d, "dd.MM.yyyy");
  } catch {
    return typeof value === "string" ? value : "";
  }
}

export function AbsenceConfirmPopup2({
  visible,
  onHiding,
  filteredSlots,
  isHR,
  departments,
  selectedDepartment,
  setSelectedDepartment,
  searchQuery,
  setSearchQuery,
  onConfirm,
  onReject,
  onConfirmAll,
  onRejectAll,
}: AbsenceConfirmPopup2Props) {
  const { t } = useTranslation();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;
  const [expandedEmployee, setExpandedEmployee] = useState<string | null>(null);

  const askConfirm = (message: string, action: () => void) => {
    confirm(message, t("common.confirmation")).then((value) => {
      if (value === true) {
        action();
      }
    });
  };

  const groupedByEmployee = useMemo(() => {
    const groups = new Map<string, PendingSlot2[]>();
    for (const slot of filteredSlots) {
      const key = `${slot.lastName ?? ""} ${slot.firstName ?? ""}`.trim();
      const existing = groups.get(key);
      if (existing) {
        existing.push(slot);
      } else {
        groups.set(key, [slot]);
      }
    }
    return Array.from(groups.entries());
  }, [filteredSlots]);

  const allIds = useMemo(
    () =>
      filteredSlots
        .map((s) => Number(s.id))
        .filter((id) => !Number.isNaN(id)),
    [filteredSlots],
  );

  return (
    <Popup
      dragEnabled={!isMobile}
      className="popup-ava"
      showCloseButton
      visible={visible}
      onHiding={onHiding}
      fullScreen={isMobile}
      width={isMobile ? "100vw" : "90vw"}
      height={isMobile ? "100vh" : "auto"}
      maxWidth={isMobile ? undefined : 1200}
      maxHeight={isMobile ? undefined : "90vh"}
      title={`${t("holidayPopup.confirmHoliday")} (${filteredSlots.length})`}
    >
      {isMobile ? (
        <div className="flex flex-col gap-3 p-1">
          {isHR ? (
            <SelectBox
              width="100%"
              items={["", ...departments]}
              value={selectedDepartment}
              placeholder={t("Wybierz dział")}
              displayExpr={(item: string) =>
                item === "" ? t("Wszystkie działy") : item
              }
              valueExpr="this"
              onValueChanged={(e) => setSelectedDepartment(e.value ?? "")}
              showClearButton
            />
          ) : null}
          <TextBox
            width="100%"
            value={searchQuery}
            placeholder={t("Wyszukaj pracownika")}
            showClearButton
            onValueChanged={(e) => setSearchQuery(e.value ?? "")}
          />
          <div className="flex items-center justify-between px-1 text-sm">
            <span className="font-semibold">
              {`${t("holidayPopup.toConfirm")}: ${filteredSlots.length}`}
            </span>
          </div>
          {allIds.length > 0 ? (
            <Button
              width="100%"
              icon="check"
              type="default"
              text={t("acceptAll")}
              onClick={() =>
                askConfirm(t("holidayPopup.confirmAllGlobal"), () =>
                  onConfirmAll(allIds),
                )
              }
            />
          ) : null}
          <div className="flex flex-col gap-2 pb-6">
            {groupedByEmployee.map(([employeeName, slots]) => {
              const employeeIds = slots
                .map((s) => s.id)
                .filter((id): id is number => typeof id === "number");
              const expanded = expandedEmployee === employeeName;
              return (
                <div
                  key={employeeName}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  <div className="flex items-center gap-1 p-2 bg-gray-50 dark:bg-[#1e1e1e]">
                    <button
                      type="button"
                      className="flex-1 min-w-0 flex items-center gap-2 text-left p-1"
                      onClick={() =>
                        setExpandedEmployee((prev) =>
                          prev === employeeName ? null : employeeName,
                        )
                      }
                    >
                      <i
                        className={clsx(
                          "dx-icon",
                          expanded
                            ? "dx-icon-chevronup"
                            : "dx-icon-chevrondown",
                        )}
                      />
                      <span className="font-semibold text-sm truncate">
                        {employeeName}
                      </span>
                      <span className="text-xs opacity-70 flex-shrink-0">
                        ({slots.length})
                      </span>
                    </button>
                    <Button
                      icon="check"
                      type="default"
                      stylingMode="outlined"
                      hint={t("Zatwierdź wszystkie")}
                      onClick={() =>
                        askConfirm(
                          t("holidayPopup.confirmAllForEmployee"),
                          () => onConfirmAll(employeeIds),
                        )
                      }
                    />
                    <Button
                      icon="trash"
                      type="danger"
                      stylingMode="outlined"
                      hint={t("Odrzuć wszystkie")}
                      onClick={() =>
                        askConfirm(t("holidayPopup.rejectAllForEmployee"), () =>
                          onRejectAll(employeeIds),
                        )
                      }
                    />
                  </div>
                  {expanded ? (
                    <div className="flex flex-col gap-2 p-3 bg-[#fffbe6] dark:bg-[#3a3208]">
                      {slots.map((slot) => (
                        <div
                          key={String(slot.id)}
                          className="rounded-md border border-yellow-300 dark:border-yellow-700 p-3 bg-white dark:bg-[#2a2a2a]"
                        >
                          <div className="flex flex-col gap-1 text-sm">
                            <div className="font-semibold break-words">
                              {slot.name || "-"}
                            </div>
                            <div className="opacity-85">
                              <span className="opacity-60 mr-1">
                                {t("holidayPopup.startDate")}:
                              </span>
                              {formatSlotDate(slot.from)}
                              <span className="mx-1 opacity-60">→</span>
                              <span className="opacity-60 mr-1">
                                {t("holidayPopup.endDate")}:
                              </span>
                              {formatSlotDate(slot.to)}
                            </div>
                            {slot.note ? (
                              <div className="text-xs opacity-80 break-words">
                                <span className="opacity-60 mr-1">
                                  {t("holidayPopup.note")}:
                                </span>
                                {slot.note}
                              </div>
                            ) : null}
                          </div>
                          <div className="flex gap-2 mt-3">
                            <Button
                              width="100%"
                              icon="check"
                              type="default"
                              text={t("holidayPopup.accept")}
                              onClick={() => onConfirm(Number(slot.id))}
                            />
                            <Button
                              width="100%"
                              icon="trash"
                              type="danger"
                              text={t("holidayPopup.reject")}
                              onClick={() => onReject(Number(slot.id))}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {groupedByEmployee.length === 0 ? (
              <div className="p-4 text-sm text-blue-800 rounded-lg bg-blue-100 dark:bg-gray-800 dark:text-blue-400">
                {t("common.noData")}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <ScrollView width="100%" height="100%" showScrollbar="always">
          <DataGrid
            dataSource={filteredSlots}
            keyExpr="id"
            columnAutoWidth
            allowColumnResizing
            columnResizingMode="widget"
            showBorders
            wordWrapEnabled
            hoverStateEnabled
            showRowLines
            showColumnLines
            rowAlternationEnabled
            grouping={{ autoExpandAll: true }}
            groupPanel={{ visible: true }}
            onRowPrepared={(e) => {
              if (e.rowType === "data") {
                e.rowElement.style.background = "#fffbe6";
              }
            }}
            toolbar={{
              items: [
                {
                  location: "before",
                  widget: "dxSelectBox",
                  visible: isHR,
                  options: {
                    items: ["", ...departments],
                    value: selectedDepartment,
                    placeholder: t("Wybierz dział"),
                    displayExpr: (item: string) =>
                      item === "" ? t("Wszystkie działy") : item,
                    valueExpr: "this",
                    onValueChanged: (e: { value: string }) =>
                      setSelectedDepartment(e.value),
                    width: 200,
                    showClearButton: true,
                  },
                },
                {
                  location: "before",
                  widget: "dxTextBox",
                  options: {
                    value: searchQuery,
                    placeholder: t("Wyszukaj pracownika"),
                    showClearButton: true,
                    onValueChanged: (e: { value: string }) =>
                      setSearchQuery(e.value),
                    width: 200,
                  },
                },
                {
                  location: "center",
                  template: () => (
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: 16,
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      {t("holidayPopup.toConfirm")}: {filteredSlots.length}
                    </div>
                  ),
                },
                {
                  location: "after",
                  widget: "dxButton",
                  options: {
                    icon: "check",
                    text: t("acceptAll"),
                    type: "default",
                    stylingMode: "contained",
                    onClick: () => {
                      if (allIds.length) onConfirmAll(allIds);
                    },
                  },
                },
              ],
            }}
          >
            <Column
              dataField="lastName"
              caption={t("Pracownik")}
              calculateGroupValue={(data: PendingSlot2) =>
                `${data.lastName ?? ""} ${data.firstName ?? ""}`
              }
              groupIndex={0}
              visible={false}
              groupCellRender={({ value }: { value: string }) => {
                const employeeRows = filteredSlots.filter(
                  (x) => `${x.lastName} ${x.firstName}` === value,
                );
                const employeeIds = employeeRows
                  .map((x) => x.id)
                  .filter((id): id is number => typeof id === "number");

                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 8,
                      width: "100%",
                    }}
                  >
                    <div style={{ fontWeight: "bold" }}>
                      {t("Pracownik")}: {value}
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Button
                        icon="check"
                        text={t("Zatwierdź wszystkie")}
                        type="default"
                        stylingMode="contained"
                        onClick={() => onConfirmAll(employeeIds)}
                      />
                      <Button
                        icon="trash"
                        text={t("Odrzuć wszystkie")}
                        type="danger"
                        stylingMode="contained"
                        onClick={() => onRejectAll(employeeIds)}
                      />
                    </div>
                  </div>
                );
              }}
            />

            <Column
              caption={t("Imię i nazwisko")}
              allowGrouping={false}
              width={200}
              cellRender={({ data }: { data: PendingSlot2 }) => (
                <div className="font-semibold">
                  {data.lastName} {data.firstName}
                </div>
              )}
            />

            <Column dataField="name" caption={t("Typ urlopu")} width={200} />
            <Column
              dataField="from"
              caption={t("holidayPopup.startDate")}
              dataType="date"
              format="dd.MM.yyyy"
              sortOrder="asc"
              sortIndex={0}
              width={120}
            />
            <Column
              dataField="to"
              caption={t("holidayPopup.endDate")}
              dataType="date"
              format="dd.MM.yyyy"
              width={120}
            />
            <Column
              dataField="note"
              caption={t("holidayPopup.note")}
              hidingPriority={0}
              cellRender={({ value }: { value: string }) => (
                <span className="text-gray-600 text-sm">{value || "-"}</span>
              )}
            />

            <Column
              caption={t("Akcje")}
              width={120}
              allowGrouping={false}
              cellRender={({
                row,
              }: {
                row: { rowType: string; data: PendingSlot2; rowIndex: number };
              }) => {
                if (row.rowType !== "data") return null;
                return (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 4,
                    }}
                  >
                    <Button
                      icon="check"
                      hint={t("holidayPopup.accept")}
                      stylingMode="text"
                      onClick={() => onConfirm(Number(row.data.id))}
                    />
                    <Button
                      icon="trash"
                      hint={t("holidayPopup.reject")}
                      stylingMode="text"
                      onClick={() => onReject(Number(row.data.id))}
                    />
                  </div>
                );
              }}
            />

            <FilterRow visible />
            <Paging defaultPageSize={15} />
            <Pager
              showInfo
              showPageSizeSelector
              allowedPageSizes={[10, 15, 30, 50]}
            />
          </DataGrid>
        </ScrollView>
      )}
    </Popup>
  );
}
